/**
 * Detects if an image has significant transparent areas.
 * Uses canvas sampling for performance - analyzes at reduced size.
 *
 * @param imageUrl - URL of the image to analyze
 * @param transparencyThreshold - Minimum ratio of transparent pixels to consider image as having transparency (default 0.05 = 5%)
 * @returns Promise<boolean> - true if image has significant transparency
 */
function detectImageTransparency(imageUrl: string, transparencyThreshold = 0.05): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        // Sample at small size for performance (max 100x100)
        const sampleSize = 100;
        const scale = Math.min(sampleSize / img.width, sampleSize / img.height, 1);
        canvas.width = Math.max(1, Math.floor(img.width * scale));
        canvas.height = Math.max(1, Math.floor(img.height * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(false);
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const { data } = imageData;

        // Count pixels with transparency (alpha < 250)
        let transparentPixels = 0;
        // Alpha channel is every 4th value starting at index 3
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 250) {
            transparentPixels += 1;
          }
        }

        const totalPixels = data.length / 4;
        const transparencyRatio = transparentPixels / totalPixels;

        resolve(transparencyRatio > transparencyThreshold);
      } catch {
        // CORS or other errors - assume no transparency
        resolve(false);
      }
    };

    img.onerror = () => {
      resolve(false);
    };

    img.src = imageUrl;
  });
}

export default detectImageTransparency;
